import {
  getGrammarPosition,
  getParaphrasePosition,
} from '@chegg-wt/core-wa/utils/helpers'
import { Feedback } from '@chegg-wt/core-wa/types'
import getFeatureEditor from '@chegg-wt/core-wa/utils/getEditor'
import {
  DEBOUNCE_SAVE_PAPER_TIME_PERIOD,
  FeedbackTab,
} from '@chegg-wt/core-wa/constants/paraphraser'
import fullPaperChecker from '@chegg-wt/core-wa/utils/pusherDataHandlers/fullPaperCheckHandler'
import { CheckTypeInput } from '@chegg-wt/core/globalTypesOneGraph'
import { Dispatch } from 'redux'
import { debounce } from '@chegg-wt/core/utils/functions'
import history from '@chegg-wt/core-wa/utils/history/history'
import { store } from '@chegg-wt/core/state' // ? will need store dispatch, logic dispatch does not work for pusher subscribed events
import { PusherGrammarFeedback, PusherParaphraseFeedback, WtPusherEvent } from './types'
import { appendFeedback, updateTotalFeedbackCount, updatePaperHandler } from './actions'
import { activeFeedbackTabSelector, sentenceOffsetsSelector } from './selectors'

export const handlePusherGrammarEvent = (
  eventData: WtPusherEvent<PusherGrammarFeedback>
) => {
  const { dispatch, getState } = store
  const editor = getFeatureEditor()
  const state = getState()
  const sentenceOffsets = sentenceOffsetsSelector(state)
  const isGrammarTabActive = activeFeedbackTabSelector(state) === FeedbackTab.GRAMMAR_TAB

  const grammarFeedbacks: Feedback[] = eventData.feedback.suggestions.map(
    (grammarSuggestion) => ({
      id: grammarSuggestion.id,
      sentenceId: eventData.sentence.id,
      feedbackType: eventData.feedback.feedbackType,
      changeType: grammarSuggestion.changeType,
      position: grammarSuggestion.position,
      suggestedText: grammarSuggestion.suggestedText,
    })
  )

  // special handing of discarded cards
  const sentence = sentenceOffsets.find(
    (sentence) => sentence.id === eventData.sentence.id
  )
  const filteredGrammarFeedbacks = history.filterRemovedGrammarCardsFromFeedbacks(
    eventData.sentence.id,
    sentence.originalText,
    grammarFeedbacks
  )

  // add to feedback
  dispatch(appendFeedback(filteredGrammarFeedbacks))
  // todo - sort feedback on every event. quick sort as sorting is to be done on multiple elements in one go

  // update underline if grammar tab active
  if (isGrammarTabActive) {
    filteredGrammarFeedbacks.forEach((grammarFeedback) => {
      const { offset, length } = getGrammarPosition(grammarFeedback, sentenceOffsets)

      editor.commands.setCustomUnderline({
        id: grammarFeedback.id,
        offset,
        length,
        type: grammarFeedback.feedbackType,
      })
    })
  }
  dispatch(updateTotalFeedbackCount())
}

export const handlePusherParaphraseEvent = (
  eventData: WtPusherEvent<PusherParaphraseFeedback>
) => {
  const { dispatch, getState } = store
  const state = getState()
  const sentenceOffsets = sentenceOffsetsSelector(state)

  // Not consider paraphrase if we don't get any sentence or sentence is same
  const sentence = sentenceOffsets.find(
    (sentence) => sentence.id === eventData.sentence.id
  )
  if (
    !eventData.feedback.suggestedText ||
    eventData.feedback.suggestedText === sentence?.originalText
  )
    return

  const isParaphraseTabActive =
    activeFeedbackTabSelector(state) === FeedbackTab.PARAPHRASE_TAB

  const editor = getFeatureEditor()

  // handle paraphrase feedback
  const newFeedback: Feedback = {
    id: eventData.feedback.id,
    sentenceId: eventData.sentence.id,
    feedbackType: eventData.feedback.feedbackType,
    suggestedText: eventData.feedback.suggestedText,
    reason: eventData.feedback.reason,
  }

  // special handing of discarded cards
  // True means this is already discarded and we don't need to consider this paraphrase feedback
  if (
    history.filterRemovedParaphraseCardsFromFeedbacks(
      eventData.sentence.id,
      sentence.originalText
    )
  ) {
    return
  }
  // add to feedback
  dispatch(appendFeedback([newFeedback]))
  // todo - sort feedback on every event. insertion sort as one only element to be placed in already sorted array

  // update underline based on active tab
  if (isParaphraseTabActive) {
    const { offset, length } = getParaphrasePosition(newFeedback, sentenceOffsets)

    editor.commands.setCustomUnderline({
      id: newFeedback.id,
      offset,
      length,
      type: newFeedback.feedbackType,
    })
  }
  dispatch(updateTotalFeedbackCount())
}

export const handlePusherEventData = (eventData: WtPusherEvent) => {
  const { dispatch } = store
  if (eventData.checkType === CheckTypeInput.FULL) {
    fullPaperChecker.handleFeedbackEvent(eventData, dispatch)
  }
}

export const onSavePaper = (dispatch: Dispatch) => {
  dispatch(updatePaperHandler())
}

export const debounceOnSavePaper = debounce(
  (dispatch: Dispatch) => onSavePaper(dispatch),
  DEBOUNCE_SAVE_PAPER_TIME_PERIOD
)
